import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environments';
import { ProjectSettingsView, ProjectView, PublicProjectView, WarehouseView } from '@shared/models/domain/domain-view.model';
import { Address, UserCreditCard } from '@shared/models/domain/domain.model';
import { Project } from '@shared/models/domain/wms.model';
import { PageRequest } from '@shared/models/request/page-request.model';
import { PageResponse } from '@shared/models/response/page-response.model';
import { map, Observable, of, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PublicService {
  private httpClient = inject(HttpClient);
  protected baseUrl = environment.apiUrl;

  getPublicProjectViewById(projectId: string): Observable<ProjectView | null> {
    if (!projectId) return of(null);
    return this.httpClient.get<ProjectView>(`${this.baseUrl}api/view/project/${projectId}`);
  }

  getPublicProjectViewByToken(projectId: string, tokenHash: string): Observable<PublicProjectView | null> {
    if (!tokenHash || !projectId) return of(null);
    return this.httpClient.get<PageResponse<PublicProjectView>>(`${this.baseUrl}api/view/public-project/${projectId}/token/${tokenHash}`)
      .pipe(
        map((response) => response.item)
      );
  }

  getPublicWarehouseView(warehouseId: string, tokenHash: string): Observable<WarehouseView | null> {
    if (!tokenHash || !warehouseId) return of(null);
    return this.httpClient.get<PageResponse<WarehouseView>>(`${this.baseUrl}api/customer/view/warehouse/${warehouseId}/token/${tokenHash}`)
      .pipe(
        map((response) => response.item)
      );
  }

  signStorageAgreement(projectId: string, tokenHash: string, payload: Project): Observable<Project | null> {
    if (!tokenHash || !projectId) return of(null);
    return this.httpClient.post<Project>(`${this.baseUrl}api/project/${projectId}/sign-storage-agreement/token/${tokenHash}`, payload);
  }

  getPublicProjectSettings(tokenHash: string, request: PageRequest): Observable<ProjectSettingsView[]> {
    if (!tokenHash) return of<ProjectSettingsView[]>([]);
    const pageRequest = new PageRequest().deserialize(request ?? {});
    return this.httpClient.get<PageResponse<ProjectSettingsView[]>>(`${this.baseUrl}api/customer/view/project-settings/token/${tokenHash}`, { params: pageRequest.toParams() })
      .pipe(
        map((response) => response.item ?? [])
      );
  }

  getPublicProjectCreditCards(projectId: string, tokenHash: string): Observable<UserCreditCard[]> {
    if (!tokenHash || !projectId) return of<UserCreditCard[]>([]);
    return this.httpClient.get<PageResponse<UserCreditCard>>(`${this.baseUrl}api/project/${projectId}/credit-card/token/${tokenHash}`)
      .pipe(
        map((response) => (response.item ?? []) as UserCreditCard[])
      );
  }

  addPublicProjectCreditCard(projectId: string, tokenHash: string, payload: { externalCardIdNonce: string; last4DigitsCC: string; cardType: string; nickname?: string }): Observable<UserCreditCard | null> {
    if (!tokenHash || !projectId) return of(null);
    return this.httpClient.post<PageResponse<UserCreditCard>>(`${this.baseUrl}api/project/${projectId}/credit-card/token/${tokenHash}`, payload)
      .pipe(map((response) => response.item ?? null));
  }

  getPublicAddressByExternalId(tokenHash: string, address: Address): Observable<Address | null> {
    if (!tokenHash || !address?.externalPlaceId) return of(null);

    const queryParams = {
      'find[externalPlaceId]': address.externalPlaceId,
      currentPage: 1,
      itemsPerPage: 1,
    };

    return this.httpClient.get<PageResponse<Address[]>>(`${this.baseUrl}api/address/token/${tokenHash}`, { params: queryParams })
      .pipe(
        switchMap((response) => {
          const existingAddress = response?.item?.[0] ?? null;
          if (existingAddress?.addressId) return of(existingAddress);

          return this.httpClient.post<PageResponse<Address>>(`${this.baseUrl}api/address/token/${tokenHash}`, address)
            .pipe(
              map((createResponse) => createResponse?.item ?? null)
            );
        })
      );
  }
}
